import React, { useState, useEffect, useRef } from 'react';
import { X, Bell, BellOff, Globe, Eye, EyeOff, Palette, Sparkles, Check, Upload, Trash2, Loader2, Edit2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import EditableTranslation from './EditableTranslation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SettingsModal = ({ isOpen, onClose, onSettingsChange }) => {
  const { user, token, updateProfile, canWrite } = useAuth();
  const { language, setLanguage: setGlobalLanguage, t, isKinyarwanda } = useLanguage();
  const { theme, setTheme, fontSize, setFontSize, availableThemes } = useTheme();

  const [settings, setSettings] = useState(() => {
    const saved = localStorage.getItem('yegoNewsSettings');
    const parsed = saved ? JSON.parse(saved) : {
      notifications: true,
      autoPlayVideos: false,
      showImages: true,
      language: 'en'
    };
    return parsed;
  });

  // Sync settings language with global language context
  useEffect(() => {
    if (settings.language !== language) {
      const newSettings = { ...settings, language };
      setSettings(newSettings);
      localStorage.setItem('yegoNewsSettings', JSON.stringify(newSettings));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  
  // Profile states (for contributors)
  const [bio, setBio] = useState(user?.bio || '');
  const [name, setName] = useState(user?.name || '');
  const [editingName, setEditingName] = useState(false);
  const [savingName, setSavingName] = useState(false);
  const [profilePicture, setProfilePicture] = useState(user?.profile_picture_url || null);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [savingBio, setSavingBio] = useState(false);
  const [profileMessage, setProfileMessage] = useState({ type: '', text: '' });
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (user?.bio !== undefined) setBio(user.bio || '');
    if (user?.name !== undefined) setName(user.name || '');
    if (user?.profile_picture_url !== undefined) setProfilePicture(user.profile_picture_url || null);
  }, [user?.bio, user?.name, user?.profile_picture_url]);

  const compressImage = (file, maxDim = 300) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new window.Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const minDim = Math.min(img.width, img.height);
          const startX = (img.width - minDim) / 2;
          const startY = (img.height - minDim) / 2;
          const size = Math.min(minDim, maxDim);
          canvas.width = size;
          canvas.height = size;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, startX, startY, minDim, minDim, 0, 0, size, size);
          canvas.toBlob((blob) => {
            if (blob) {
              resolve(new File([blob], 'avatar.jpg', { type: 'image/jpeg' }));
            } else {
              resolve(file);
            }
          }, 'image/jpeg', 0.85);
        };
        img.onerror = () => resolve(file);
        img.src = event.target.result;
      };
      reader.onerror = () => resolve(file);
      reader.readAsDataURL(file);
    });
  };

  const handleProfilePictureUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setProfileMessage({ type: 'error', text: 'Please select an image file' });
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setProfileMessage({ type: 'error', text: 'Image must be less than 10MB' });
      return;
    }

    setUploadingPicture(true);
    setProfileMessage({ type: '', text: '' });

    try {
      const optimizedFile = await compressImage(file, 300);
      const formData = new FormData();
      formData.append('file', optimizedFile);
      const response = await axios.post(`${BACKEND_URL}/api/auth/upload-profile-picture`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      const newUrl = response.data.profile_picture_url;
      setProfilePicture(newUrl);
      if (updateProfile) {
        updateProfile({ profile_picture_url: newUrl, avatar: newUrl });
      }
      setProfileMessage({ type: 'success', text: 'Photo updated!' });
    } catch (error) {
      console.error('Profile picture upload error:', error);
      const errMsg = error.response?.data?.detail || 'Failed to upload';
      setProfileMessage({ type: 'error', text: errMsg });
    } finally {
      setUploadingPicture(false);
    }
  };

  const handleDeleteProfilePicture = async () => {
    try {
      await axios.delete(`${BACKEND_URL}/api/auth/profile-picture`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfilePicture(null);
      if (updateProfile) {
        updateProfile({ profile_picture_url: null });
      }
      setProfileMessage({ type: 'success', text: 'Photo removed!' });
    } catch (error) {
      setProfileMessage({ type: 'error', text: 'Failed to delete photo' });
    }
  };

  const handleSaveBio = async () => {
    setSavingBio(true);
    setProfileMessage({ type: '', text: '' });
    try {
      await axios.put(`${BACKEND_URL}/api/auth/profile`, { bio }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (updateProfile) {
        updateProfile({ bio });
      }
      setProfileMessage({ type: 'success', text: 'Bio saved!' });
    } catch (error) {
      setProfileMessage({ type: 'error', text: 'Failed to save bio' });
    } finally {
      setSavingBio(false);
    }
  };

  const handleSaveName = async () => {
    if (!name.trim()) return;
    setSavingName(true);
    setProfileMessage({ type: '', text: '' });
    try {
      await axios.put(`${BACKEND_URL}/api/auth/profile`, { name: name.trim() }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (updateProfile) {
        updateProfile({ name: name.trim() });
      }
      setEditingName(false);
      setProfileMessage({ type: 'success', text: 'Name updated!' });
    } catch (error) {
      setProfileMessage({ type: 'error', text: 'Failed to update name' });
    } finally {
      setSavingName(false);
    }
  };

  const handleSettingChange = (key, value) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    localStorage.setItem('yegoNewsSettings', JSON.stringify(newSettings));
    
    // If language changed, update the global language context
    if (key === 'language') {
      setGlobalLanguage(value);
    }
    
    // Notify parent component of settings change
    if (onSettingsChange) {
      onSettingsChange(newSettings);
    }
    
    // Dispatch custom event for components to listen to
    window.dispatchEvent(new CustomEvent('yegoNewsSettingsChange', { 
      detail: newSettings 
    }));
  };

  if (!isOpen) return null;

  // Get translated labels
  const settingsTitle = t('settings.title') || 'Settings';
  const appearanceLabel = t('settings.appearance') || 'Theme & Appearance';
  const fontSizeLabel = t('settings.fontSize') || 'Text Size';
  const fontSizeDesc = t('settings.fontSizeDesc') || 'Scale text across all news & articles';
  const preferencesLabel = t('settings.preferences') || 'Preferences';
  const notificationsLabel = t('settings.notifications') || 'Notifications';
  const notificationsDesc = t('settings.notificationsDesc') || 'Get alerts for breaking news';
  const showImagesLabel = t('settings.showImages') || 'Show Images';
  const showImagesDesc = t('settings.showImagesDesc') || 'Display article thumbnails';
  const languageLabel = t('settings.language') || 'Language';
  const languageDesc = t('settings.languageDesc') || 'Choose your preferred language';
  const doneText = t('btn.done') || 'Done';

  // Font size options with preview scales
  const fontSizeOptions = [
    { id: 'small', label: isKinyarwanda ? 'Ntoya' : 'Small', preview: '14px', sample: 'Aa' },
    { id: 'medium', label: isKinyarwanda ? 'Hagati' : 'Medium', preview: '16px', sample: 'Aa' },
    { id: 'large', label: isKinyarwanda ? 'Nini' : 'Large', preview: '18.5px', sample: 'Aa' },
  ];

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-md z-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
        <div 
          className="bg-white dark:bg-gray-900 rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden border border-gray-200/80 dark:border-gray-800 transition-all max-h-[90vh] flex flex-col"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-800/80">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl bg-emerald-500/10 flex items-center justify-center text-emerald-500">
                <Sparkles className="h-4 w-4" />
              </div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                <EditableTranslation translationKey="settings.title">
                  {settingsTitle}
                </EditableTranslation>
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>

          {/* Scrollable Content */}
          <div className="p-6 space-y-6 overflow-y-auto flex-1 scrollbar-thin">
            {/* Profile / Author Section (for all logged in users, authors, contributors, admins) */}
            {user && (
              <div className="pb-5 mb-2 border-b border-gray-100 dark:border-gray-800/80">
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">
                  {canWrite || user?.role === 'contributor' || user?.role === 'author' || user?.is_admin
                    ? (isKinyarwanda ? "Umwirondoro w'umwanditsi" : "Author Profile")
                    : (isKinyarwanda ? "Umwirondoro wanjye" : "My Profile")}
                </h3>

                {profileMessage.text && (
                  <div className={`p-2.5 rounded-lg text-xs mb-3 flex items-center gap-1.5 ${
                    profileMessage.type === 'success' 
                      ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400' 
                      : 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400'
                  }`}>
                    {profileMessage.type === 'success' ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
                    <span>{profileMessage.text}</span>
                  </div>
                )}

                <div className="flex items-start gap-4">
                  <div className="relative flex-shrink-0">
                    <div className="w-16 h-16 rounded-full overflow-hidden bg-gradient-to-br from-emerald-400 to-blue-500 flex items-center justify-center border-2 border-emerald-500 shadow-sm">
                      {profilePicture ? (
                        <img src={profilePicture} alt="Profile" className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-white text-2xl font-bold">
                          {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                        </span>
                      )}
                    </div>
                    {uploadingPicture && (
                      <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center">
                        <Loader2 className="h-5 w-5 text-white animate-spin" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    {editingName ? (
                      <div className="flex items-center gap-2 mb-2">
                        <input
                          type="text"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          className="flex-1 px-2.5 py-1 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none text-gray-900 dark:text-white"
                          autoFocus
                        />
                        <button onClick={handleSaveName} disabled={savingName} className="p-1.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg">
                          {savingName ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                        </button>
                        <button onClick={() => { setEditingName(false); setName(user?.name || ''); }} className="p-1.5 bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-lg">
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-semibold text-gray-900 dark:text-white text-sm truncate">{user?.name}</p>
                        <button onClick={() => setEditingName(true)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded text-gray-400 hover:text-emerald-500" title="Edit name">
                          <Edit2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    )}
                    <p className="text-xs text-emerald-600 dark:text-emerald-400 capitalize mb-2">
                      {user?.is_admin ? 'Editor / Admin' : (user?.role || 'Contributor')}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={handleProfilePictureUpload} className="hidden" />
                      <button 
                        type="button" 
                        onClick={() => fileInputRef.current?.click()} 
                        disabled={uploadingPicture} 
                        className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white text-xs font-semibold rounded-lg flex items-center gap-1.5 shadow-sm transition-colors cursor-pointer"
                      >
                        <Upload className="h-3.5 w-3.5" />
                        {profilePicture ? 'Change Photo' : 'Upload Photo'}
                      </button>
                      {profilePicture && (
                        <button 
                          type="button" 
                          onClick={handleDeleteProfilePicture} 
                          className="px-2.5 py-1.5 bg-red-50 hover:bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 text-xs font-semibold rounded-lg flex items-center gap-1 border border-red-200 dark:border-red-800 transition-colors"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                <div className="pt-3 mt-3 border-t border-gray-100 dark:border-gray-800/80">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <Edit2 className="h-3 w-3 text-gray-400" />
                    <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                      {isKinyarwanda ? 'Amagambo make akwereka (Bio)' : 'Author Bio'}
                    </span>
                  </div>
                  <textarea
                    value={bio}
                    onChange={(e) => setBio(e.target.value)}
                    placeholder={isKinyarwanda ? "Andika amagambo make akwereka..." : "Write a short bio about yourself as an author..."}
                    className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-sm text-gray-900 dark:text-white resize-none focus:ring-2 focus:ring-emerald-500 outline-none"
                    rows={2}
                    maxLength={200}
                  />
                  <div className="flex items-center justify-between mt-1.5">
                    <span className="text-[11px] text-gray-400">{bio.length}/200</span>
                    <button 
                      type="button" 
                      onClick={handleSaveBio} 
                      disabled={savingBio} 
                      className="px-3.5 py-1 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-xs font-semibold rounded-lg flex items-center gap-1 shadow-sm transition-colors"
                    >
                      {savingBio ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                      {isKinyarwanda ? 'Bika' : 'Save Bio'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Appearance / Themes */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs font-bold text-gray-400 dark:text-gray-400 uppercase tracking-wider">
                  <EditableTranslation translationKey="settings.appearance">
                    {appearanceLabel}
                  </EditableTranslation>
                </h3>
                <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                  {availableThemes.length} Themes
                </span>
              </div>

              {/* Theme Grid Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                {availableThemes.map((tItem) => {
                  const isActive = theme === tItem.id;
                  return (
                    <button
                      key={tItem.id}
                      onClick={() => setTheme(tItem.id)}
                      className={`relative p-3 rounded-2xl border text-left transition-all group overflow-hidden ${
                        isActive
                          ? 'border-emerald-500 ring-2 ring-emerald-500/30 shadow-md scale-[1.02]'
                          : 'border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700 bg-gray-50/50 dark:bg-gray-800/40'
                      }`}
                      style={{
                        boxShadow: isActive && tItem.glowColor ? `0 0 16px ${tItem.glowColor}` : undefined
                      }}
                    >
                      {/* Swatch Header */}
                      <div className="flex items-center justify-between mb-2">
                        <div
                          className="w-5 h-5 rounded-full border shadow-inner flex items-center justify-center"
                          style={{
                            backgroundColor: tItem.previewBg,
                            borderColor: tItem.previewBorder,
                            boxShadow: tItem.glowColor ? `0 0 8px ${tItem.glowColor}` : 'none'
                          }}
                        >
                          <div
                            className="w-2 h-2 rounded-full"
                            style={{ backgroundColor: tItem.previewAccent }}
                          />
                        </div>
                        {isActive && (
                          <div className="w-4 h-4 rounded-full bg-emerald-500 text-white flex items-center justify-center">
                            <Check className="h-2.5 w-2.5" />
                          </div>
                        )}
                      </div>

                      {/* Theme Name */}
                      <p className="text-xs font-bold text-gray-900 dark:text-white leading-snug line-clamp-1">
                        {isKinyarwanda ? tItem.nameRw : tItem.name}
                      </p>
                      <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5 leading-tight line-clamp-1">
                        {tItem.description}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Font Size Scaling */}
            <div className="pt-2 border-t border-gray-100 dark:border-gray-800/80">
              <div className="flex items-center gap-3 mb-2.5">
                <div className="w-7 h-7 rounded-lg bg-purple-500/10 flex items-center justify-center text-purple-500">
                  <Palette className="h-4 w-4" />
                </div>
                <div>
                  <p className="font-semibold text-sm text-gray-900 dark:text-white">
                    <EditableTranslation translationKey="settings.fontSize">
                      {fontSizeLabel}
                    </EditableTranslation>
                  </p>
                  <p className="text-xs text-gray-500">
                    <EditableTranslation translationKey="settings.fontSizeDesc">
                      {fontSizeDesc}
                    </EditableTranslation>
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2 mt-3">
                {fontSizeOptions.map(opt => {
                  const isActive = fontSize === opt.id;
                  return (
                    <button
                      key={opt.id}
                      onClick={() => setFontSize(opt.id)}
                      className={`py-3 px-3 rounded-2xl text-center border transition-all ${
                        isActive
                          ? 'border-emerald-500 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 font-bold ring-2 ring-emerald-500/20'
                          : 'border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700 text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      <div className="text-lg mb-0.5" style={{ fontSize: opt.preview }}>
                        {opt.sample}
                      </div>
                      <div className="text-xs font-medium">{opt.label}</div>
                      <div className="text-[10px] text-gray-400 mt-0.5">{opt.preview}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Preferences */}
            <div className="pt-2 border-t border-gray-100 dark:border-gray-800/80 space-y-4">
              <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                <EditableTranslation translationKey="settings.preferences">
                  {preferencesLabel}
                </EditableTranslation>
              </h3>
              
              {/* Notifications */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-7 h-7 rounded-lg bg-blue-500/10 flex items-center justify-center text-blue-500">
                    {settings.notifications ? <Bell className="h-4 w-4" /> : <BellOff className="h-4 w-4 text-gray-400" />}
                  </div>
                  <div>
                    <p className="font-semibold text-sm text-gray-900 dark:text-white">
                      <EditableTranslation translationKey="settings.notifications">
                        {notificationsLabel}
                      </EditableTranslation>
                    </p>
                    <p className="text-xs text-gray-500">
                      <EditableTranslation translationKey="settings.notificationsDesc">
                        {notificationsDesc}
                      </EditableTranslation>
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => handleSettingChange('notifications', !settings.notifications)}
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    settings.notifications ? 'bg-emerald-500' : 'bg-gray-300 dark:bg-gray-700'
                  }`}
                >
                  <span
                    className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                      settings.notifications ? 'translate-x-7' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {/* Show Images */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-7 h-7 rounded-lg bg-emerald-500/10 flex items-center justify-center text-emerald-500">
                    {settings.showImages ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4 text-gray-400" />}
                  </div>
                  <div>
                    <p className="font-semibold text-sm text-gray-900 dark:text-white">
                      <EditableTranslation translationKey="settings.showImages">
                        {showImagesLabel}
                      </EditableTranslation>
                    </p>
                    <p className="text-xs text-gray-500">
                      <EditableTranslation translationKey="settings.showImagesDesc">
                        {showImagesDesc}
                      </EditableTranslation>
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => handleSettingChange('showImages', !settings.showImages)}
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    settings.showImages ? 'bg-emerald-500' : 'bg-gray-300 dark:bg-gray-700'
                  }`}
                >
                  <span
                    className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                      settings.showImages ? 'translate-x-7' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {/* Language */}
              <div className="pt-2">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-7 h-7 rounded-lg bg-cyan-500/10 flex items-center justify-center text-cyan-500">
                    <Globe className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="font-semibold text-sm text-gray-900 dark:text-white">
                      <EditableTranslation translationKey="settings.language">
                        {languageLabel}
                      </EditableTranslation>
                    </p>
                    <p className="text-xs text-gray-500">
                      <EditableTranslation translationKey="settings.languageDesc">
                        {languageDesc}
                      </EditableTranslation>
                    </p>
                  </div>
                </div>
                <select
                  value={settings.language}
                  onChange={(e) => handleSettingChange('language', e.target.value)}
                  className="w-full mt-1 px-4 py-2.5 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-sm font-medium text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 outline-none"
                >
                  <option value="en">English</option>
                  <option value="rw">Kinyarwanda</option>
                  <option value="fr">Français</option>
                  <option value="sw">Kiswahili</option>
                </select>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-4 px-6 border-t border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-900/50">
            <button
              onClick={onClose}
              className="w-full py-3 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white font-semibold rounded-2xl shadow-lg shadow-emerald-500/20 transition-all hover:scale-[1.01] active:scale-[0.99]"
            >
              <EditableTranslation translationKey="btn.done">
                {doneText}
              </EditableTranslation>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default SettingsModal;
